import React from "react";
import { getDashboardTableData } from "../../../Actions/DashboardActions";
import { useQuery } from "@tanstack/react-query";
import NoData from "../../../components/NoData";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "@mui/material";

// Progress bar component
const ProgressBar = ({ current, total }) => {
  const percentage = total > 0 ? Math.min((current / total) * 100, 100) : 0;
  const getColor = () => {
    if (percentage >= 100) return "bg-green-500";
    if (percentage >= 75) return "bg-blue-500";
    if (percentage >= 50) return "bg-yellow-500";
    return "bg-orange-500";
  };

  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`h-full ${getColor()} transition-all duration-300`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="text-xs text-[#666] whitespace-nowrap">
        {current}/{total}
      </span>
    </div>
  );
};

// Status badge component
const StatusBadge = ({ status }) => {
  const getStatusStyle = () => {
    switch (status?.toLowerCase()) {
      case "live":
        return "bg-green-100 text-green-700 border-green-200";
      case "paused":
        return "bg-yellow-100 text-yellow-700 border-yellow-200";
      case "completed":
        return "bg-blue-100 text-blue-700 border-blue-200";
      case "draft":
        return "bg-gray-100 text-gray-700 border-gray-200";
      default:
        return "bg-gray-100 text-gray-600 border-gray-200";
    }
  };

  return (
    <span
      className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${getStatusStyle()}`}
    >
      {status || "Draft"}
    </span>
  );
};

const MySurveys = ({ user }) => {
  const navigate = useNavigate();
  const {
    data: dashboardTableData,
    isLoading: dashboardTableDataLoading,
    isError: dashboardTableDataError,
  } = useQuery({
    queryFn: () => getDashboardTableData(user?.organizationId),
    queryKey: ["dashboardSurveyTable", user?.organizationId],
    enabled: !!user?.organizationId,
    staleTime: 10000,
    cacheTime: 15000,
  });

  return (
    <div className="w-full bg-white border border-[#E0E0E0] rounded-xl h-[420px] flex flex-col overflow-hidden">
      <div className="px-6 py-4 border-b border-[#E0E0E0] flex items-center justify-between bg-gray-50/50">
        <div>
          <h1 className="text-[#0226BE] text-[16px] font-[500]">My Surveys</h1>
          <p className="text-[11px] text-[#666] mt-0.5">
            Recent survey performance
          </p>
        </div>
        <button
          className="text-[#0226BE] text-[13px] font-[500] hover:bg-blue-50 px-3 py-1.5 rounded-lg transition-all cursor-pointer flex items-center gap-1"
          onClick={(e) => navigate("/surveys")}
        >
          See all
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>
      <div className="flex-1 overflow-y-auto containerScroll">
        <table className="w-full">
          <thead className="bg-gray-50/80 sticky top-0 z-10">
            <tr className="border-b border-[#E0E0E0]">
              {[
                "Survey",
                "Progress",
                "Completes",
                "AVG. LOI",
                "Conv. Rate",
                "I.R",
                "Avg. CPI",
                "Drop-off",
              ]?.map((data, ind) => (
                <th
                  className="text-[#555] px-4 py-3 text-[11px] font-[600] text-left uppercase tracking-wide"
                  key={ind}
                >
                  {data}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dashboardTableDataLoading ? (
              <tr>
                <td colSpan={8}>
                  <div className="w-full text-center py-6 flex items-center justify-center flex-col space-y-3 h-[250px]">
                    <div className="w-8 h-8 border-2 border-[#0226BE] border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-[#666] text-[13px]">
                      Loading surveys...
                    </p>
                  </div>
                </td>
              </tr>
            ) : dashboardTableDataError ? (
              <tr>
                <td colSpan={8}>
                  <div className="w-full text-center py-6 flex items-center justify-center flex-col space-y-3 h-[250px]">
                    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                      <svg
                        className="w-6 h-6 text-red-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                    </div>
                    <p className="text-[#e12121] text-[13px] font-medium">
                      Something went wrong!
                    </p>
                  </div>
                </td>
              </tr>
            ) : dashboardTableData?.length ? (
              dashboardTableData?.map((stats, key) => (
                <tr
                  className="border-b border-[#E0E0E0] hover:bg-blue-50/40 transition-colors cursor-pointer group"
                  key={key}
                  onClick={(e) => navigate(`/surveys/${stats?._id}/details`)}
                >
                  <td className="px-4 py-3">
                    <Tooltip title={stats?.surveyName} placement="top">
                      <div className="flex flex-col gap-1">
                        <h1 className="text-[#333] font-[500] text-[13px] truncate max-w-[140px] group-hover:text-[#0226BE] transition-colors">
                          {stats?.surveyName || "-"}
                        </h1>
                        <StatusBadge status={stats?.surveyStatus} />
                      </div>
                    </Tooltip>
                  </td>
                  <td className="px-4 py-3">
                    <ProgressBar
                      current={stats?.completes || 0}
                      total={stats?.expectedMatrix?.expectedCompletes || 0}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-[#333] font-[600] text-[13px]">
                      {stats?.completes || 0}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[#666] font-[400] text-[13px]">
                    {stats?.avgLoi
                      ? `${Number(stats?.avgLoi)?.toFixed(1)} min`
                      : "-"}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-[13px] font-[500] ${
                        stats?.conversion > 50
                          ? "text-green-600"
                          : stats?.conversion > 25
                          ? "text-yellow-600"
                          : "text-red-600"
                      }`}
                    >
                      {stats?.conversion
                        ? `${Number(stats?.conversion)?.toFixed(1)}%`
                        : "-"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[#666] font-[400] text-[13px]">
                    {stats?.IR ? `${Number(stats?.IR)?.toFixed(1)}%` : `-`}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-[#333] font-[500] text-[13px]">
                      {stats?.avgCPI
                        ? `$${Number(stats?.avgCPI)?.toFixed(2)}`
                        : `-`}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-[13px] font-[500] ${
                        stats?.dropRate > 50
                          ? "text-red-600"
                          : stats?.dropRate > 25
                          ? "text-yellow-600"
                          : "text-green-600"
                      }`}
                    >
                      {stats?.dropRate
                        ? `${Number(stats?.dropRate)?.toFixed(1)}%`
                        : `-`}
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8}>
                  <NoData msg="No surveys found. Create your first survey!" />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MySurveys;
